// Clawd pets mode controller.
//
// Renders an animated Clawd SVG character (loaded from jsDelivr) instead of an
// emoji glyph. The SitePet core still drives position (walk FSM) and drag, so
// Clawd roams the page like the classic pet, but its emotion is expressed by
// swapping the Clawd SVG state image. Each Clawd SVG ships its own internal
// <style> animations, so no extra CSS animation is required here.
//
// State filenames are verified against the upstream asset set
// (abderrahimghazali/clawd-pet, public/pets/clawd-{state}.svg).
//
// The upstream repo ships 112+ distinct non-numeric states (emotions, hobbies,
// seasonal themes, working personas). For idle moods the controller cycles
// through a large randomised selection so the pet feels lively and varied.

import type { Behavior, ControllerContext, Mood, PetController, Vec2 } from './types';

const CLAWD_CDN_BASE =
  'https://cdn.jsdelivr.net/gh/abderrahimghazali/clawd-pet@main/public/pets';

type ClawdState = string;

const CLAWD_FALLBACK = 'static-base';

// ── State pools ──────────────────────────────────────────────────────────────
// Deterministic moods map one-to-one to a specific Clawd expression.
const DETERMINISTIC: Record<string, ClawdState> = {
  walking:  'running',
  sleeping: 'sleeping',
  alert:    'surprised',
  playful:  'laughing',
  startled: 'scared',
  happy:    'happy',
  sad:      'sad',
};

// Fun, neutral or positive states shown while the pet is idling.  Picked
// randomly on each idle transition and cycled every ~20 s.
const IDLE_POOL: readonly string[] = [
  'smile', 'happy', 'cool', 'coffee', 'dancing', 'music',
  'camping', 'gaming', 'coding', 'reading', 'painting',
  'photography', 'surfing', 'yoga', 'meditating', 'gardening',
  'fishing', 'star', 'rainbow', 'magic', 'ninja', 'pirate',
  'superhero', 'king', 'astronaut', 'chef', 'detective', 'dj',
  'drumming', 'driving', 'flying', 'swimming', 'climbing',
  'flexing', 'bowling', 'singing', 'studying', 'crafting',
  'rocket', 'umbrella', 'clapping', 'celebrating', 'charging',
  'ice-cream', 'eating', 'shipping', 'podcast', 'waving',
  'love', 'snow', 'spring', 'summer', 'autumn', 'winter',
  'halloween', 'christmas', 'birthday', 'thanksgiving',
  'trophy', 'time-travel', 'fire', 'skateboard', 'gift',
  'going-away', 'valentine', 'new-year', 'bored',
  'peeking', 'chatting', 'hopeful', 'joy',
];

// Intellectually-tinged states shown while the pet is thinking.
const THINKING_POOL: readonly string[] = [
  'idea', 'confused', 'studying', 'reading', 'coding',
  'detective', 'telescope', 'hallucinating', 'facepalm',
  'skeptical', 'shrug', 'mindblown',
  'working-thinking', 'working-debugger', 'working-rubber-duck',
  'working-wizard', 'working-confused',
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function clawdUrl(state: string): string {
  return `${CLAWD_CDN_BASE}/clawd-${state}.svg`;
}

function pickRandom(arr: readonly string[]): string {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ── Controller factory ───────────────────────────────────────────────────────

export function createClawdController(ctx: ControllerContext): PetController {
  const { el } = ctx;

  el.classList.add('site-pet-clawd');

  const img = document.createElement('img');
  img.className = 'site-pet-clawd-img';
  img.alt = '';
  img.decoding = 'async';
  img.draggable = false;

  let currentState: ClawdState = 'happy';
  let lastRequestedSrc = clawdUrl(currentState);
  img.src = lastRequestedSrc;

  const onImgError = () => {
    const fallback = clawdUrl(CLAWD_FALLBACK);
    if (!img.src.endsWith(`clawd-${CLAWD_FALLBACK}.svg`)) {
      lastRequestedSrc = fallback;
      img.src = fallback;
    }
  };
  img.addEventListener('error', onImgError);

  el.appendChild(img);

  function setState(state: ClawdState) {
    if (state === currentState) return;
    currentState = state;
    cycleTimer = 0;
    const next = clawdUrl(state);
    lastRequestedSrc = next;
    img.src = next;
  }

  // ── Mood tracking & idle cycling ───────────────────────────────────────────

  let lastMood: Mood | null = null;
  let cycleTimer = 0;

  // Tick budget: ~60 ticks/s.  Idle cycles every ~20 s, thinking every ~30 s.
  const IDLE_CYCLE_TICKS = 1200;
  const THINK_CYCLE_TICKS = 1800;

  // ── Horizontal facing ──────────────────────────────────────────────────────

  let lastX = ctx.initialPos.x;
  let facingLeft = false;

  function currentX(): number {
    return parseFloat(el.style.left) || ctx.initialPos.x;
  }

  // ── Controller API ─────────────────────────────────────────────────────────

  return {
    drivesPosition: true,
    draggable: true,

    setMood(mood: Mood) {
      if (mood === lastMood) return;

      lastMood = mood;

      if (mood === 'idle' || mood === 'resting') {
        setState(pickRandom(IDLE_POOL));
      } else if (mood === 'thinking') {
        setState(pickRandom(THINKING_POOL));
      } else {
        setState(DETERMINISTIC[mood] ?? 'smile');
      }
    },

    setBehavior(behavior: Behavior) {
      if (behavior === 'walking') lastX = currentX();
    },

    tick() {
      // ── Idle state cycling ──────────────────────────────────────────────
      if (lastMood === 'idle' || lastMood === 'resting') {
        if (++cycleTimer >= IDLE_CYCLE_TICKS) {
          setState(pickRandom(IDLE_POOL));
        }
      } else if (lastMood === 'thinking') {
        if (++cycleTimer >= THINK_CYCLE_TICKS) {
          setState(pickRandom(THINKING_POOL));
        }
      }

      // ── Walking direction mirroring ─────────────────────────────────────
      const x = currentX();
      const dx = x - lastX;
      if (Math.abs(dx) > 0.5) {
        const nextFacingLeft = dx < 0;
        if (nextFacingLeft !== facingLeft) {
          facingLeft = nextFacingLeft;
          img.style.transform = facingLeft ? 'scaleX(-1)' : '';
        }
        lastX = x;
      }
    },

    getPosition(): Vec2 {
      const x = currentX();
      const y = parseFloat(el.style.top) || ctx.initialPos.y;
      return {
        x: x + (el.offsetWidth || 40) / 2,
        y: y + (el.offsetHeight || 40) / 2,
      };
    },

    destroy() {
      img.removeEventListener('error', onImgError);
      img.remove();
      el.classList.remove('site-pet-clawd');
    },
  };
}
